/**
 * Notification Utilities
 *
 * This file provides utility functions for displaying notifications
 * for success, error, warning, and info messages using Ant Design.
 *
 * These functions use the App component's notification and message APIs
 * to avoid the "Static function can not consume context like dynamic theme" warning.
 */
import { NotificationPlacement } from 'antd/es/notification/interface';

// Default notification configuration
const defaultNotificationConfig = {
  placement: 'topRight' as NotificationPlacement,
  duration: 5,
};

// Get the notification and message instances from the App component
// These will be set by the NotificationProvider component
let notification: any = null;
let message: any = null;

/**
 * Initialize the notification and message instances
 * This should be called by the NotificationProvider component
 */
export const initNotificationInstances = (notificationInstance: any, messageInstance: any) => {
  notification = notificationInstance;
  message = messageInstance;
};

/**
 * Show a success notification
 * @param title - The title of the notification
 * @param description - Optional description for the notification
 */
export const showSuccessNotification = (title: string, description?: string) => {
  if (notification) {
    notification.success({
      message: title,
      description,
      ...defaultNotificationConfig,
    });
  }

  // Also show a message for immediate feedback
  if (message) {
    message.success(title);
  }
};

/**
 * Show an error notification
 * @param title - The title of the notification
 * @param description - Optional description for the notification
 */
export const showErrorNotification = (title: string, description?: string) => {
  if (notification) {
    notification.error({
      message: title,
      description,
      ...defaultNotificationConfig,
    });
  }

  // Also show a message for immediate feedback
  if (message) {
    message.error(title);
  }
};

/**
 * Show a warning notification
 * @param title - The title of the notification
 * @param description - Optional description for the notification
 */
export const showWarningNotification = (title: string, description?: string) => {
  if (notification) {
    notification.warning({
      message: title,
      description,
      ...defaultNotificationConfig,
    });
  }

  // Also show a message for immediate feedback
  if (message) {
    message.warning(title);
  }
};

/**
 * Show an info notification
 * @param title - The title of the notification
 * @param description - Optional description for the notification
 */
export const showInfoNotification = (title: string, description?: string) => {
  if (notification) {
    notification.info({
      message: title,
      description,
      ...defaultNotificationConfig,
    });
  }

  // Also show a message for immediate feedback
  if (message) {
    message.info(title);
  }
};
